// SPDX-FileCopyrightText: Szilárd Hajba
// SPDX-License-Identifier: LGPL-3.0-or-later

import { getFileUrl } from '@cloudillo/core'
import {
	EmptyState,
	Fcd,
	IcImage,
	LoadingSpinner,
	LoadMoreTrigger,
	PageHeader,
	useAuth,
	useDebouncedValue,
	VBox
} from '@cloudillo/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentContextIdTag } from '../../context/index.js'
import { DrawerToggle } from '../../ui/DrawerToggle.js'
import { BLANK_IMAGE_SRC } from '../../utils.js'
import { ActiveFilters } from './components/ActiveFilters.js'
import { GalleryGrid } from './components/GalleryGrid.js'
import { GallerySidebar } from './components/GallerySidebar.js'
import { GalleryToolbar } from './components/GalleryToolbar.js'
import { useGalleryFilters } from './hooks/useGalleryFilters.js'
import { useGalleryImages } from './hooks/useGalleryImages.js'

export function GalleryApp() {
	const { t } = useTranslation()
	const [auth] = useAuth()
	const contextIdTag = useCurrentContextIdTag()

	// Filter state with URL sync
	const {
		filters,
		setViewMode,
		setTimeFilter,
		setLayout,
		setSort,
		toggleTag,
		clearTags,
		clearAll,
		hasActiveFilters,
		apiQueryParams
	} = useGalleryFilters()

	// UI state
	const [showFilter, setShowFilter] = React.useState(false)
	const [searchQuery, setSearchQuery] = React.useState('')
	const fileName = useDebouncedValue(searchQuery, 300).trim()
	const isFiltered = hasActiveFilters || !!fileName

	// Use infinite scroll hook for images
	const {
		photos: files,
		isLoading,
		isLoadingMore,
		error,
		hasMore,
		loadMore,
		sentinelRef
	} = useGalleryImages({
		apiQueryParams: fileName ? { ...apiQueryParams, fileName } : apiQueryParams
	})

	// Convert files to photo format for the grid
	const photos = React.useMemo(
		() =>
			files?.map((f) => {
				const idTag = contextIdTag || auth?.idTag || ''
				const fileId = f.fileId
				return {
					// Grid preview: use vis.sd (640px) for good quality thumbnails.
					// A refused fileId becomes a blank tile rather than a dropped one, so
					// the grid stays index-aligned with `files`.
					src: getFileUrl(idTag, fileId, 'vis.sd') ?? BLANK_IMAGE_SRC,
					// Lightbox fullscreen: use vis.hd (1920px) for high quality
					fullSrc: getFileUrl(idTag, fileId, 'vis.hd'),
					width: f.x?.dim?.[0] || 100,
					height: f.x?.dim?.[1] || 100,
					title: f.x?.caption || f.fileName
				}
			}),
		[files, contextIdTag, auth?.idTag]
	)

	// Render content based on state
	const renderContent = () => {
		// Loading state (initial load only)
		if (isLoading && files.length === 0) {
			return (
				<VBox align="center" justify="center" className="h-100">
					<LoadingSpinner size="lg" label={t('Loading gallery...')} />
				</VBox>
			)
		}

		// Empty state (no photos at all)
		if (files.length === 0 && !isFiltered) {
			return (
				<EmptyState
					icon={<IcImage />}
					title={t('No images found')}
					description={t('Upload some images to see them here')}
				/>
			)
		}

		// Empty state (filters applied but no results)
		if (files.length === 0 && isFiltered) {
			return (
				<EmptyState
					icon={<IcImage />}
					title={t('No photos match your filters')}
					description={t('Try adjusting your filters to see more photos')}
				/>
			)
		}

		// Photo grid with LoadMoreTrigger - FcdContent handles scrolling
		return (
			<>
				<GalleryGrid photos={photos || []} layout={filters.layout} className="p-3" />
				<LoadMoreTrigger
					ref={sentinelRef}
					isLoading={isLoadingMore}
					hasMore={hasMore}
					error={error}
					onRetry={loadMore}
					loadingLabel={t('Loading more images...')}
					retryLabel={t('Retry')}
					errorPrefix={t('Failed to load:')}
				/>
			</>
		)
	}

	return (
		<Fcd.Container className="overflow-hidden">
			<Fcd.Filter isVisible={showFilter} hide={() => setShowFilter(false)}>
				<GallerySidebar
					searchQuery={searchQuery}
					onSearchQueryChange={setSearchQuery}
					viewMode={filters.viewMode}
					onViewModeChange={setViewMode}
					timeFilter={filters.timeFilter}
					onTimeFilterChange={setTimeFilter}
					selectedTags={filters.selectedTags}
					onTagToggle={toggleTag}
					onClearTags={clearTags}
				/>
			</Fcd.Filter>

			<Fcd.Content
				width="fluid"
				surface="raised"
				header={
					<VBox gap={2} autoBg>
						<PageHeader
							title={t('Gallery')}
							actions={<DrawerToggle nav onClick={() => setShowFilter(true)} />}
						/>
						<GalleryToolbar
							layout={filters.layout}
							onLayoutChange={setLayout}
							sort={filters.sort}
							sortDir={filters.sortDir}
							onSortChange={setSort}
						/>
					</VBox>
				}
			>
				{files.length > 0 && (
					<ActiveFilters
						className="auto-bg"
						viewMode={filters.viewMode}
						selectedTags={filters.selectedTags}
						timeFilter={filters.timeFilter}
						onRemoveTag={toggleTag}
						onClearTimeFilter={() => setTimeFilter('all')}
						onClearViewMode={() => setViewMode('all')}
						onClearAll={() => {
							clearAll()
							setSearchQuery('')
						}}
						totalCount={undefined}
						filteredCount={files.length}
					/>
				)}

				{renderContent()}
			</Fcd.Content>

			{/* No Fcd.Details - gallery uses fluid width */}
		</Fcd.Container>
	)
}

// vim: ts=4
